
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Save, Plus, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ContactForm from "../contacts/ContactForm";
import { appClient } from "@/api/appClient";
import { useCatalog } from "@/hooks/useCatalog";
import { useSalesUsers } from "@/hooks/useSalesUsers";

export default function DealForm({ deal, onSubmit, onCancel, isProcessing, moduleSettings, initialContactId }) {
  const { t } = useTranslation();
  const { items: catalogItems, loading: catalogLoading } = useCatalog();
  const { salesUsers, isLoading: isLoadingSalesUsers } = useSalesUsers();
  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
  
  const [formData, setFormData] = useState({
    contact_id: "",
    stage: "new",
    catalog_id: "",
    name: "",
    sales_consultant: "",
    sales_development: ""
  });

  useEffect(() => {
    loadContacts();
  }, []);

  // Handle initial contact selection from URL
  useEffect(() => {
    const loadInitialContact = async () => {
      if (initialContactId && !selectedContact) {
        // Primeiro tenta na lista local (rápido)
        const localContact = contacts.find(c => c.id === initialContactId);
        if (localContact) {
          handleSelectContact(localContact);
          return;
        }
        
        // Se não encontrou localmente e já carregou a lista, busca direto do banco
        if (contacts.length > 0 || !isLoadingContacts) {
          console.log('[DealForm] Contact not in local list, fetching from database:', initialContactId);
          const { data, error } = await supabase
            .from('contacts')
            .select('*')
            .eq('id', initialContactId)
            .maybeSingle();
          
          if (!error && data) {
            handleSelectContact(data);
          }
        }
      }
    };
    
    loadInitialContact();
  }, [initialContactId, contacts.length, selectedContact, isLoadingContacts]);

  const loadContacts = async () => {
    setIsLoadingContacts(true);
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error("Error loading contacts:", error);
    }
    setIsLoadingContacts(false);
  };

  const handleCreateContact = async (contactData) => {
    setIsCreatingContact(true);
    try {
      const { data, error } = await supabase
        .from('contacts')
        .insert([contactData])
        .select()
        .single();
      
      if (error) throw error;
      
      await loadContacts();
      setSelectedContact(data);
      const contactName = `${data.first_name || ''} ${data.last_name || ''}`.trim();
      setFormData({ 
        ...formData, 
        contact_id: data.id,
        name: contactName
      });
      setShowContactForm(false);
      setSearchQuery("");
    } catch (error) {
      console.error("Error creating contact:", error);
    }
    setIsCreatingContact(false);
  };

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
    setFormData({ 
      ...formData, 
      contact_id: contact.id,
      name: contactName
    });
    setSearchQuery("");
  };

  const submittingRef = useRef(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || isProcessing) return;
    submittingRef.current = true;
    setTimeout(() => { submittingRef.current = false; }, 2000);
    
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!appUser) throw new Error("User profile not found");

      const dealData = {
        contact_id: formData.contact_id,
        sales_consultant: formData.sales_consultant || appUser.id,
        sales_development: formData.sales_development || null,
        source: 'pipeline',
        stage: formData.stage,
        total_amount: selectedCatalogItem?.base_price || 0,
        currency: 'USD',
        notes: null,
        name: formData.name || `${selectedContact?.first_name || ''} ${selectedContact?.last_name || ''}`.trim()
      };
      
      onSubmit(dealData, formData.catalog_id || null);
    } catch (error) {
      console.error("Error in deal form:", error);
      alert("Error creating deal: " + error.message);
    }
  };

  // Busca no servidor com debounce - suporta busca por nome completo
  useEffect(() => {
    const searchContacts = async () => {
      const query = searchQuery.trim();
      if (!query) {
        setSearchResults([]);
        return;
      }
      
      setIsSearching(true);
      
      // Divide a busca em palavras para suportar "Nome Sobrenome"
      const words = query.split(/\s+/).filter(w => w.length > 0);
      
      let supabaseQuery = supabase
        .from('contacts')
        .select('*')
        .eq('status', 'active');
      
      // Para cada palavra, adiciona um filtro AND que busca em qualquer campo
      words.forEach(word => {
        supabaseQuery = supabaseQuery.or(
          `first_name.ilike.%${word}%,last_name.ilike.%${word}%,primary_email.ilike.%${word}%,primary_phone.ilike.%${word}%`
        );
      });
      
      const { data, error } = await supabaseQuery
        .order('first_name', { ascending: true })
        .limit(20);
      
      setIsSearching(false);
      if (!error && data) {
        setSearchResults(data);
      }
    };
    
    const timer = setTimeout(searchContacts, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Contact Selection */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-slate-700 font-medium">
              {t('dealForm.selectContact')} *
            </Label>
            <Button
              type="button"
              onClick={() => setShowContactForm(true)}
              variant="outline"
              size="sm"
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('dealForm.newContact')}
            </Button>
          </div>

          {/* Selected Contact Display */}
          {selectedContact && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900">
                  {selectedContact.first_name} {selectedContact.last_name}
                </p>
                <p className="text-sm text-slate-600">
                  {selectedContact.email || selectedContact.primary_phone}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedContact(null);
                  setFormData({ ...formData, contact_id: "", name: "" });
                }}
                className="text-slate-600"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Deal Name Field */}
          {selectedContact && (
            <div className="space-y-2">
              <Label htmlFor="deal_name">{t('dealForm.dealName')}</Label>
              <Input
                id="deal_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('dealForm.placeholders.dealName')}
                className="border-slate-300"
              />
              <p className="text-xs text-slate-500">
                {t('dealForm.dealNameHint')}
              </p>
            </div>
          )}

          {/* Search Box */}
          {!selectedContact && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder={t('dealForm.placeholders.searchContacts')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-slate-300"
                />
              </div>

              {/* Search Results */}
              {isSearching && (
                <div className="p-4 text-center text-slate-500 text-sm border border-slate-200 rounded-lg">
                  {t('dealForm.loadingContacts')}
                </div>
              )}

              {!isSearching && searchQuery && searchResults.length > 0 && (
                <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
                  {searchResults.map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => handleSelectContact(contact)}
                      className="w-full p-4 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
                    >
                      <p className="font-semibold text-slate-900">
                        {contact.first_name} {contact.last_name}
                      </p>
                      <p className="text-sm text-slate-600">
                        {contact.email || contact.primary_phone}
                      </p>
                    </button>
                  ))}
                </div>
              )}

              {!isSearching && searchQuery && searchResults.length === 0 && (
                <div className="p-4 text-center text-slate-500 text-sm border border-slate-200 rounded-lg">
                  {t('dealForm.noContactsFound')}
                </div>
              )}
            </>
          )}
        </div>

        {/* Catalog Item Selection */}
        <div className="space-y-2">
          <Label htmlFor="catalog_id">{t('dealForm.catalogItem')}</Label>
          <Select
            value={formData.catalog_id || "none"}
            onValueChange={(value) => {
              if (value === "none") {
                setSelectedCatalogItem(null);
                setFormData({ ...formData, catalog_id: "" });
              } else {
                const item = catalogItems.find(i => i.id === value);
                setSelectedCatalogItem(item);
                setFormData({ ...formData, catalog_id: value });
              }
            }}
          >
            <SelectTrigger className="border-slate-300">
              <SelectValue placeholder={t('dealForm.placeholders.selectCatalogItem')} />
            </SelectTrigger>
            <SelectContent>
              {catalogLoading ? (
                <SelectItem value="loading" disabled>{t('common.loading')}</SelectItem>
              ) : (
                <>
                  <SelectItem value="none">{t('common.none')}</SelectItem>
                  {catalogItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} - ${item.base_price || 0}
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
          {selectedCatalogItem && (
            <p className="text-sm text-slate-600">
              {t('dealForm.type')}: {selectedCatalogItem.type} | {t('dealForm.price')}: ${selectedCatalogItem.base_price || 0}
            </p>
          )}
        </div>

        {/* Stage Selection */}
        <div className="space-y-2">
          <Label htmlFor="stage">{t('dealForm.dealStage')} *</Label>
          <Select
            value={formData.stage}
            onValueChange={(value) => setFormData({ ...formData, stage: value })}
            required
          >
            <SelectTrigger className="border-slate-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">{t('stages.new')}</SelectItem>
              <SelectItem value="contact">{t('stages.contact')}</SelectItem>
              <SelectItem value="scheduled">{t('stages.scheduled')}</SelectItem>
              <SelectItem value="proposal">{t('stages.proposal')}</SelectItem>
              <SelectItem value="negotiation">{t('stages.negotiation')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sales Consultant Selection */}
        <div className="space-y-2">
          <Label htmlFor="sales_consultant">{t('dealForm.salesConsultant')}</Label>
          <Select
            value={formData.sales_consultant || "auto"}
            onValueChange={(value) => setFormData({ ...formData, sales_consultant: value === "auto" ? "" : value })}
          >
            <SelectTrigger className="border-slate-300">
              <SelectValue placeholder={t('dealForm.placeholders.autoAssign')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">{t('dealForm.placeholders.autoAssign')}</SelectItem>
              {isLoadingSalesUsers ? (
                <SelectItem value="loading" disabled>{t('common.loading')}</SelectItem>
              ) : (
                salesUsers
                  .filter(u => u.system_role?.code === 'sales_consultant' || u.system_role?.code === 'sales_manager')
                  .map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                    </SelectItem>
                  ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Sales Development Selection */}
        <div className="space-y-2">
          <Label htmlFor="sales_development">{t('dealForm.salesDevelopment')}</Label>
          <Select
            value={formData.sales_development || "unassigned"}
            onValueChange={(value) => setFormData({ ...formData, sales_development: value === "unassigned" ? "" : value })}
          >
            <SelectTrigger className="border-slate-300">
              <SelectValue placeholder={t('dealForm.placeholders.selectSdr')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">{t('common.unassigned')}</SelectItem>
              {isLoadingSalesUsers ? (
                <SelectItem value="loading" disabled>{t('common.loading')}</SelectItem>
              ) : (
                salesUsers
                  .filter(u => u.system_role?.code === 'sales_dev')
                  .map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                    </SelectItem>
                  ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Info Message */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            💡 {selectedCatalogItem ? t('dealForm.infoWithItem') : t('dealForm.infoWithoutItem')}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
            className="border-slate-300"
          >
            <X className="w-4 h-4 mr-2" />
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isProcessing || !formData.contact_id}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isProcessing ? t('dealForm.creating') : t('dealForm.createDeal')}
          </Button>
        </div>
      </form>

      {/* Contact Creation Modal */}
      <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('dealForm.createNewContact')}</DialogTitle>
          </DialogHeader>
          <ContactForm
            onSubmit={handleCreateContact}
            onCancel={() => setShowContactForm(false)}
            isProcessing={isCreatingContact}
            simplified={true}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
